import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import Inspector, { Band, Facts, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import StatusPill from '../../components/nucleus/StatusPill';
import { usePlace } from '../../nucleus/place';
import { sharingState, grantHref } from './peopleData';
import { PersonFace } from '../../components/nucleus/faces';
import { ago, dateOf } from '../../nucleus/format';
import { schemaLabel } from '../../utils/schemaUtils';

const enc = encodeURIComponent;

// A friendship that ended, with nothing open between you since.
const ended = (e) => !!(e.past && !e.friend && !e.incoming && !e.sent);

// How one side of a friendship stands, said from its owner's side. One side
// revoked or past its end date leaves the friendship standing (only both sides
// ended makes it past), so either side may read ended. Theirs names them.
export const ENDED = new Set(['expired', 'revoked']);
const MINE_WORD = { paused: 'You paused', expired: 'Yours ended', revoked: 'Yours ended', none: 'You share nothing' };
const theirsWord = (state, name) => ({
    paused: 'They paused', expired: `${name}’s sharing ended`, revoked: `${name}’s sharing ended`, none: 'They share nothing',
})[state];
// when a side ended: revoked, or its end date reached, whichever came first
const endedOn = (ep) => [ep?.revoked_at, ep?.expires_at].filter(Boolean)
    .map((t) => new Date(t)).filter((d) => d <= new Date()).sort((x, y) => x - y)[0] || null;

// WHAT A PERSON IS TO YOU, in a few words: the inspector's tag, a cell's hover,
// a table's Relationship and Find's note all read this one. What they are,
// then how it stands — one kind, one state, as an app's reads "App ·
// Connected". Your side first: it is the one you can act on. The order is the
// panel's: a friend, a friend request, a friendship that ended, then follows.
// A request, either way, reads as one set: which kind, then whose move it is
// ("Friend request · Waiting on you", "Follow request · Waiting on them").
export function tagOf(e) {
    if (!e) return '';
    if (e.self) return 'You';
    if (e.friend) {
        const mine = sharingState(e.friend.mine).state;
        const theirs = sharingState(e.friend.theirs).state;
        // WHICH SIDE THE TAG SAYS, when both are not live. An ended side outranks a
        // paused one: an ending is permanent until renewed, a pause is undone by
        // Resume. Both ended is a past friendship. Yours ended and theirs paused
        // says "Yours ended": both outrank their pause, and yours is the one you
        // can act on (Renew). Then both paused, then yours, then theirs.
        const mineEnded = ENDED.has(mine);
        const theirsEnded = ENDED.has(theirs);
        if (mineEnded && theirsEnded) return 'Past friend';
        if (mineEnded) return `Friend · ${MINE_WORD[mine]}`;
        if (theirsEnded) return `Friend · ${theirsWord(theirs, e.name)}`;
        if (mine === 'paused' && theirs === 'paused') return 'Friend · Both paused';
        // a state only when something is not live
        const word = MINE_WORD[mine] || theirsWord(theirs, e.name);
        return word ? `Friend · ${word}` : 'Friend';
    }
    if (e.incoming) return 'Friend request · Waiting on you';
    if (e.sent) return 'Friend request · Waiting on them';
    if (ended(e)) return 'Past friend';
    if (e.followAsked && !e.follower) return 'Follow request · Waiting on you';
    if (e.followSent && !e.following) return 'Follow request · Waiting on them';
    if (e.following && e.follower) return 'Follow each other';
    if (e.following) return 'You follow';
    if (e.follower) return 'Follows you';
    return e.groups?.length ? 'In your groups' : 'On ofSelf';
}

// EVERY ACT ON ONE PERSON, once — the inspector's bar and the cell's
// right-click read this same array. Anything that shares goes through the
// consent page and comes back to `back` (the page, the person still open).
export function personActions(e, { navigate, back, toast, reload, stale }) {
    if (!e || e.self) return [];
    const name = e.name;
    const grant = (q, done) => navigate(grantHref(q, back, done));
    const done = (message) => { toast.success(message); reload?.(); };
    // a request answered is one fewer waiting: the People badge is read again now
    const answered = (message) => { done(message); stale?.(); };
    const acts = [];
    // A FRIEND REQUEST TO YOU is answered, nothing else: Accept or Decline
    if (e.incoming && !e.friend) {
        return [{ id: 'accept', label: 'Accept', primary: true, run: () => grant(`follow_id=${enc(e.incoming.id)}`, 'accepted') }, {
            id: 'decline', label: 'Decline',
            confirm: { title: `Decline ${name}'s request?`, body: 'They are not told why, and they can ask again.', label: 'Decline' },
            run: async () => { await api.respondToFollow(e.incoming.id, { action: 'decline' }); answered('Request declined'); },
        }];
    }
    // YOUR FRIEND REQUEST, waiting on them: only taking it back
    if (e.sent && !e.friend) {
        return [{
            id: 'cancel', label: 'Cancel request',
            confirm: { title: `Cancel your request to ${name}?`, body: 'What you offered to share with them is withdrawn. You can ask again.', label: 'Cancel request' },
            run: async () => { await api.revokeFollow(e.sent.id); done('Request cancelled'); },
        }];
    }
    // A FRIEND, AS AN APP: what you share is a contract you can edit, pause and
    // resume. Paused comes back with Resume; ended (revoked or past its end
    // date) is renewed on the consent page, the way it was given, and the
    // friendship stands meanwhile. (Only both sides ended makes it past.)
    // Following is implicit in a friend, so a friend has no follow acts at all.
    if (e.friend) {
        const mine = sharingState(e.friend.mine).state;
        const epID = e.friend.epID;
        // both sides ended: the friendship is past (tagOf), and sharing again is a
        // new friend request, as from Past
        if (ENDED.has(mine) && ENDED.has(sharingState(e.friend.theirs).state)) {
            return [{ id: 'friend', label: 'Ask again', primary: true, run: () => grant(`user_id=${enc(e.id)}`, 'request') }];
        }
        const edit = () => grant(`user_id=${enc(e.id)}${epID ? `&edit_ep_id=${enc(epID)}` : ''}`, 'shared');
        if (mine === 'paused' && epID) {
            acts.push({ id: 'resume', label: 'Resume', primary: true, run: async () => { await api.resumeExposureProfile(epID); done(`Sharing with ${name} again`); } });
        }
        if (ENDED.has(mine)) {
            acts.push({
                id: 'renew', label: 'Renew', primary: true, run: edit,
                hint: `Renews what you share with ${name}. What ${name} shares with you is theirs to renew.`,
            });
        } else {
            acts.push({ id: 'edit', label: 'Edit access', primary: !(mine === 'paused' && epID), run: edit });
        }
        if (mine === 'live' && epID) {
            acts.push({ id: 'pause', label: 'Pause', run: async () => { await api.pauseExposureProfile(epID); done(`Sharing with ${name} is paused`); } });
        }
        acts.push({
            id: 'unfriend', label: 'Unfriend', danger: true,
            confirm: { title: `Unfriend ${name}?`, body: 'What you share with each other is revoked, both ways.', label: 'Unfriend' },
            run: async () => { await api.revokeFollow(e.friend.id); done('Friend removed'); },
        });
        return acts;
    }
    // ASKING TO FOLLOW YOU (Open follows off): accepting shows them your public page.
    // Answering comes before Add friend, which would otherwise take the first slot.
    if (e.followAsked && !e.follower) {
        acts.push({
            id: 'accept-follow', label: 'Accept', primary: true,
            run: async () => { await api.respondToFollow(e.followAsked.id, { action: 'accept' }); answered(`${name} follows you now`); },
        });
        acts.push({
            id: 'decline-follow', label: 'Decline',
            confirm: { title: `Decline ${name}'s follow request?`, body: 'They are not told, and they can ask again.', label: 'Decline' },
            run: async () => { await api.respondToFollow(e.followAsked.id, { action: 'decline' }); answered('Follow request declined'); },
        });
    }
    acts.push({ id: 'friend', label: ended(e) ? 'Ask again' : 'Add friend', primary: !e.followAsked || !!e.follower, run: () => grant(`user_id=${enc(e.id)}`, 'request') });
    if (e.followSent && !e.following) {
        acts.push({
            id: 'cancel-follow', label: 'Cancel request',
            confirm: { title: `Cancel your request to follow ${name}?`, body: 'They are not told. You can ask again.', label: 'Cancel request' },
            run: async () => { await api.revokeFollow(e.followSent.id); done('Follow request cancelled'); },
        });
    }
    if (!e.following && !e.followSent) {
        acts.push({
            id: 'follow', label: e.follower ? 'Follow back' : 'Follow',
            // with their Open follows off it waits for them to accept
            run: async () => {
                const r = await api.followUser(e.id);
                done(r?.follow?.status === 'pending' ? `Asked to follow ${name}` : `Following ${name}`);
            },
        });
    }
    if (e.following) {
        acts.push({
            id: 'unfollow', label: 'Unfollow', danger: true,
            confirm: { title: `Unfollow ${name}?`, body: 'You stop seeing their public profile. You can follow again.', label: 'Unfollow' },
            run: async () => { await api.revokeFollow(e.following.id); done(`Unfollowed ${name}`); },
        });
    }
    if (e.follower) {
        acts.push({
            id: 'remove', label: 'Remove follower', danger: true,
            confirm: { title: `Remove ${name} as a follower?`, body: 'They stop seeing your public profile.', label: 'Remove' },
            run: async () => { await api.revokeFollow(e.follower.id); done('Follower removed'); },
        });
    }
    return acts;
}

// What a friend's note says on their cell: the realm they see, or why not.
export function friendNote(tie) {
    const s = sharingState(tie.mine);
    if (s.state !== 'live') return s.state === 'none' ? 'nothing shared' : s.label.toLowerCase();
    return tie.realm ? `sees ${tie.realm}` : 'no realm';
}
export const lapsed = (tie) => sharingState(tie.mine).state !== 'live' || sharingState(tie.theirs).state !== 'live';

// One side's contract as its pill reads it (status.js's contract words).
const PILL = { live: 'active', paused: 'paused', expired: 'expired', revoked: 'revoked' };
// A side that is not live, in the facts' colours: paused waits, ended is red.
const TONE = { paused: 'warn', expired: 'live', revoked: 'live' };
// How one side stands, in a word: Active, Paused, Until <date>, Ended <date> …
const standsAs = (ep) => {
    const s = sharingState(ep);
    if (ENDED.has(s.state)) return { value: endedOn(ep) ? `Ended ${dateOf(endedOn(ep))}` : 'Ended', tone: TONE[s.state] };
    return { value: s.state === 'live' && !ep?.expires_at ? 'Active' : s.label, tone: TONE[s.state] };
};
// THE KINDS OF DATA A CONTRACT REACHES, by name, as the app labels a schema:
// "Note, Photo, Workout", the first three and "+N more". These are data types,
// never realms (their realms are theirs). '' when it reaches nothing.
const SHOWN_KINDS = 3;
export function kindNames(types) {
    const names = [...new Set((types || []).map((t) => schemaLabel(t?.schema_name)).filter(Boolean))];
    const shown = names.slice(0, SHOWN_KINDS).join(', ');
    return names.length > SHOWN_KINDS ? `${shown} +${names.length - SHOWN_KINDS} more` : shown;
}

// WHAT A CONTRACT REACHES, read off its preview (the person it is given to may
// read it): its kinds by name ('' for none). null until read, or when it
// cannot be, or is not live: a paused contract reads nothing, which says
// nothing about what it holds. A pending request's offer is paused until
// answered; its preview reads it as if accepted, `offer: true`, and `live` as
// it would then stand (false once revoked or past its date). `offer` asks for
// exactly that: live, and read as an offer.
function useKinds(epId, { offer = false } = {}) {
    const [names, setNames] = useState(null);
    useEffect(() => {
        setNames(null);
        if (!epId) return undefined;
        let live = true;
        api.request(`/exposure-profiles/${encodeURIComponent(epId)}/preview?limit=1`)
            .then((r) => {
                const counted = r?.live === true && (!offer || r.offer === true);
                if (live && counted && Array.isArray(r.types)) setNames(kindNames(r.types));
            })
            .catch(() => {});
        return () => { live = false; };
    }, [epId, offer]);
    return names;
}
// YOUR realms by name: an auto-named one ("→ Mira") is said as who it is for.
// Only ever read from your own side: the other person's realms are theirs, and
// are never shown, even when a field carries them.
const realmNames = (ep) => {
    const list = (Array.isArray(ep?.realms) ? ep.realms : []).filter((r) => r?.name)
        .map((r) => (r.auto_named ? `Only for ${r.name.replace(/^→\s*/, '')}` : r.name));
    return list.length ? list : [ep?.privacy_realm_name].filter(Boolean);
};
const ends = (ep) => (ep?.expires_at ? `Until ${dateOf(ep.expires_at)}` : 'Never ends');
// "Since 3 Mar 2026 · Until 1 Jan 2027", or "· Never ends"
const termOf = (ep, since) => [since && `Since ${dateOf(since)}`, ends(ep)].filter(Boolean).join(' · ');

// ONE PERSON, wherever they are open (Friends, Following, Find). The tag says
// what they are to you and how it stands; the bands, only what that standing
// needs: a friend's four facts and your contract as one door; a request's
// when and what; then the groups you share, and who they are. Nothing here
// ever names a realm of theirs. Every act on them is in the one bar.
export default function PersonInspector({ entry: e, actions, onClose }) {
    const { to, navigate } = usePlace();
    const f = e.friend;
    const handle = e.username && e.username !== e.name ? `@${e.username}` : null;
    const followAsk = !f && e.followAsked && !e.follower ? e.followAsked : null;
    const followOut = !f && e.followSent && !e.following ? e.followSent : null;
    const contractHref = to('realms', 'profiles', `open=person:${e.id}:`);
    const groups = Array.isArray(e.groups) ? e.groups : [];
    const about = [
        dateOf(e.joined) && { label: 'On ofSelf since', value: dateOf(e.joined) },
    ].filter(Boolean);
    // what they share, or offer, by kind: their side while it is live, or the
    // offer in their friend request
    const theirsLive = !!f && sharingState(f.theirs).state === 'live';
    const kinds = useKinds(f ? (theirsLive ? f.theirs?.id : null) : e.incoming?.theirs?.id, { offer: !f });

    let bands = null;
    if (f) {
        const mine = sharingState(f.mine);
        const theirs = standsAs(f.theirs);
        const yours = realmNames(f.mine).join(' + ') || f.realm || null;
        // "Active · Note, Photo, Workout": the kinds of data, never their realms.
        // No route shows a friend's shared nodes filtered to them, so it does not open.
        const theirsSay = theirsLive && kinds !== null ? `${theirs.value} · ${kinds || 'nothing yet'}` : theirs.value;
        // resuming your side never resumes theirs: said when both are paused
        const theirsState = sharingState(f.theirs).state;
        const bothPaused = mine.state === 'paused' && theirsState === 'paused';
        // their side ended, yours flowing: it keeps flowing until you stop it
        const onlyYours = mine.state === 'live' && ENDED.has(theirsState);
        bands = (<>
            {/* your side is the row below (realm, state, term): said once, there */}
            <Band title="Between you">
                <Facts items={[
                    { label: 'Friends since', value: dateOf(f.since) || '—' },
                    { label: `${e.name} shares`, ...theirs, value: theirsSay },
                ]} />
                {bothPaused && <Note>{`Resuming yours doesn’t resume theirs; only ${e.name} can resume what they share.`}</Note>}
                {onlyYours && <Note>{`You still share with ${e.name}. Pause or edit yours if you want that to stop.`}</Note>}
            </Band>
            {/* YOUR CONTRACT TO THEM, AS ONE DOOR (the Apps panel's "Its contract") */}
            <Band title="What you share" aside={PILL[mine.state] && <StatusPill status={PILL[mine.state]} of="contract" />}>
                <Row
                    name={mine.state === 'none' ? 'Nothing shared' : yours || 'No realm'}
                    sub={mine.state === 'none' ? undefined
                        : ENDED.has(mine.state) ? standsAs(f.mine).value
                            : termOf(f.mine, f.mine?.created_at || f.since)}
                    end={f.epID ? 'Open' : undefined}
                    onPress={f.epID ? () => navigate(contractHref) : undefined}
                />
            </Band>
        </>);
    } else if (e.incoming) {
        const theirs = e.incoming.theirs;
        // one fact: which kinds, and for how long ("Note, Photo · no end date");
        // just the term when the offer cannot be read as one
        const term = theirs?.expires_at ? `until ${dateOf(theirs.expires_at)}` : 'no end date';
        const offer = kinds !== null ? `${kinds || 'Nothing yet'} · ${term}` : term[0].toUpperCase() + term.slice(1);
        bands = (
            <Band title="Their request">
                <Facts items={[
                    { label: 'Asked', value: ago(e.incoming.at, { dateAfter: 30 }) || '—' },
                    theirs && { label: 'They offer', value: offer },
                ]} />
            </Band>
        );
    } else if (e.sent) {
        bands = (
            <Band title="Your request">
                <Facts items={[
                    { label: 'You asked', value: ago(e.sent.at, { dateAfter: 30 }) || '—' },
                    { label: 'You’ll share', value: realmNames(e.sent.mine).join(', ') || e.sent.realm || '—' },
                ]} />
                <Note>{`It starts when ${e.name} accepts and picks what to share back. Until then nothing is shared, and cancelling withdraws the offer.`}</Note>
            </Band>
        );
    } else if (ended(e)) {
        // when it ended is said here, once (the tag only says Past friend)
        bands = (
            <Band title="Between you">
                <Facts items={[{ label: 'Friends until', value: dateOf(e.past.ended) || '—' }]} />
                <Note>The friendship ended. Nothing is shared either way. Ask again sends a new friend request.</Note>
            </Band>
        );
    } else {
        // FOLLOWS, with no friendship: a follow request either way, then since when, each way
        bands = (<>
            {followAsk && (
                <Band title="Their request">
                    <Facts items={[{ label: 'Asked', value: ago(followAsk.at, { dateAfter: 30 }) || '—' }]} />
                </Band>
            )}
            {followOut && (
                <Band title="Your request">
                    <Facts items={[{ label: 'You asked', value: ago(followOut.at, { dateAfter: 30 }) || '—' }]} />
                </Band>
            )}
            {(e.following || e.follower) && (
                <Band title="Between you">
                    <Facts items={[
                        e.following && { label: 'You follow them', value: dateOf(e.following.since) ? `Since ${dateOf(e.following.since)}` : 'Yes' },
                        e.follower && { label: 'They follow you', value: dateOf(e.follower.since) ? `Since ${dateOf(e.follower.since)}` : 'Yes' },
                    ]} />
                </Band>
            )}
            {/* no tie at all (met on Find, or in a group): said, not left blank */}
            {!followAsk && !followOut && !e.following && !e.follower && (
                <Band title="Between you"><Note>Nothing between you yet.</Note></Band>
            )}
        </>);
    }

    // what answering a request does: last before who they are
    const ifAccept = !f && (e.incoming || followAsk) && (
        <Band title="If you accept">
            {e.incoming
                ? <Note>Accepting opens the consent page, where you choose what they see of you.</Note>
                : <Note>Accepting lets them see your public page. Nothing else is shared.</Note>}
        </Band>
    );

    return (
        <Inspector
            face={<PersonFace url={e.avatar} size={56} />}
            tag={tagOf(e)}
            title={e.name}
            sub={handle || undefined}
            actions={actions}
            onClose={onClose}
        >
            {e.self && <Band title="You"><Note>This is you.</Note></Band>}
            {!e.self && bands}
            {!e.self && groups.length > 0 && (
                <Band title="In your groups" aside={groups.length}>
                    {groups.map((g) => (
                        <Row key={g.id} name={g.name} sub="You are both members" onPress={() => navigate(`/groups?open=${g.id}`)} />
                    ))}
                </Band>
            )}
            {!e.self && ifAccept}
            {!e.self && about.length > 0 && (
                <Band title="About">
                    <Facts items={about} />
                </Band>
            )}
        </Inspector>
    );
}
